import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MergeDuplicatesCard = ({ token }) => {
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (dryRun) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/patients/merge-duplicates`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { dry_run: dryRun }
      });
      if (dryRun) {
        setPreview(data);
        setDone(null);
      } else {
        setDone(data);
        setPreview(null);
        toast.success(`${data.registros_eliminados} duplicados unidos`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al buscar duplicados');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="merge-duplicates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#0B3B71]">
          <Users className="h-5 w-5" />
          Unir pacientes duplicados
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Busca pacientes con el <strong>mismo nombre y teléfono</strong> (sin importar tildes, mayúsculas, espacios o guiones).
          Se conserva el registro con código; sus citas, evaluaciones, ventas y CRM pasan a él y el duplicado se elimina.
        </p>
        <Button variant="outline" onClick={() => run(true)} disabled={loading} data-testid="merge-duplicates-preview-btn">
          {loading && !preview ? 'Buscando...' : 'Buscar duplicados'}
        </Button>
        {preview && (
          <div className="space-y-3" data-testid="merge-duplicates-preview">
            <p className="text-sm text-slate-700">
              <strong data-testid="merge-duplicates-groups">{preview.grupos}</strong> pacientes repetidos ·{' '}
              <strong>{preview.registros_eliminados}</strong> registros sobrantes
            </p>
            {preview.ejemplos.length > 0 && (
              <ul className="max-h-40 overflow-y-auto text-sm bg-slate-50 rounded-lg p-3 space-y-1">
                {preview.ejemplos.map((e, i) => (
                  <li key={i}>{e.nombre} — {e.registros} registros{e.conserva_codigo ? ` (se conserva ${e.conserva_codigo})` : ''}</li>
                ))}
              </ul>
            )}
            {preview.grupos > 0 && (
              <Button onClick={() => run(false)} disabled={loading} className="bg-[#0B3B71] hover:bg-[#0B3B71]/90" data-testid="merge-duplicates-confirm-btn">
                {loading ? 'Uniendo...' : `Unir ${preview.grupos} pacientes`}
              </Button>
            )}
          </div>
        )}
        {done && (
          <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3" data-testid="merge-duplicates-result">
            Listo: {done.grupos} pacientes unidos, {done.registros_eliminados} duplicados eliminados y {done.registros_movidos} registros (citas, evaluaciones, etc.) movidos.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
